import { createSelector } from 'reselect';
import { Project } from '@/infra/types/domain';
import { latestRules } from '@/features/predicate/rule-cache';
import {
  buildProjectTodoContext,
  defaultHostFrame,
  evalFilter,
} from '@/features/predicate/walker';
import { ProjectsState, ProjectTodo } from './ducks';

type StateWithProjects = { projects: ProjectsState } | ProjectsState;

function getProjectsState(state: StateWithProjects): ProjectsState {
  return 'projects' in state && 'detail' in (state as any).projects
    ? (state as { projects: ProjectsState }).projects
    : (state as ProjectsState);
}

export const selectProjectsState = (state: StateWithProjects): ProjectsState =>
  getProjectsState(state);

export const selectProjects = createSelector(
  [selectProjectsState],
  (s): Project[] => s.projectIds.map((id) => s.projectsById[id]).filter(Boolean),
);

export const selectProjectById = (id: string) =>
  createSelector([selectProjectsState], (s): Project | null => s.projectsById[id] ?? null);

export const selectActiveProjectId = (state: StateWithProjects): string | null =>
  getProjectsState(state).detail.projectId;

export const selectActiveProject = createSelector(
  [selectProjectsState],
  (s): Project | null => (s.detail.projectId ? s.projectsById[s.detail.projectId] ?? null : null),
);

export const selectTodosById = createSelector(
  [selectProjectsState],
  (s) => s.detail.todosById,
);

export const selectTodoIds = createSelector(
  [selectProjectsState],
  (s) => s.detail.todoIds,
);

export const selectListLoading = (state: StateWithProjects): boolean =>
  getProjectsState(state).listLoading;

export const selectDetailLoading = (state: StateWithProjects): boolean =>
  getProjectsState(state).detailLoading;

export const selectError = (state: StateWithProjects): string | null =>
  getProjectsState(state).error;

// ─── Tree selectors ───────────────────────────────────────

// Sort by (sort_order ?? +Inf, created_at) so rows without explicit order
// fall to the bottom and ties resolve deterministically.
function cmpTodos(a: ProjectTodo, b: ProjectTodo): number {
  const sa = a.sort_order ?? Number.POSITIVE_INFINITY;
  const sb = b.sort_order ?? Number.POSITIVE_INFINITY;
  if (sa !== sb) return sa - sb;
  return (a.created_at || '').localeCompare(b.created_at || '');
}

// Parent → children map, each child list sorted. Memoized on todosById so
// reordering a single row rebuilds once per render pass.
export const selectChildrenMap = createSelector(
  [selectTodosById, selectTodoIds],
  (byId, ids): Record<string, ProjectTodo[]> => {
    const map: Record<string, ProjectTodo[]> = {};
    for (const id of ids) {
      const todo = byId[id];
      if (!todo) continue;
      const key = todo.parent_todo_id ?? '__root__';
      (map[key] ||= []).push(todo);
    }
    for (const k of Object.keys(map)) {
      map[k].sort(cmpTodos);
    }
    return map;
  },
);

export const selectTreeRoots = createSelector(
  [selectChildrenMap],
  (map): ProjectTodo[] => map['__root__'] ?? [],
);

export const selectChildren = (parentId: string) =>
  createSelector([selectChildrenMap], (map): ProjectTodo[] => map[parentId] ?? []);

export type RowState = 'ready' | 'blocked' | 'done';

const isClosedStatus = (status: string | undefined): boolean =>
  status === 'done' || status === 'cancelled';

// Imperative reference, kept as the cold-start fallback for
// classifyRowStates: tree is the only blocking relation — a parent is
// blocked iff any child is still open; leaves can't be blocked;
// cancelled behaves like done for unblocking ancestors. Used verbatim
// when the rule cache hasn't hydrated (so the classify.todo.* rules
// aren't available yet), so behavior never regresses before sync lands.
function classifyImperative(todos: ProjectTodo[]): Record<string, RowState> {
  const openChildCount: Record<string, number> = {};
  for (const t of todos) {
    if (t.parent_todo_id && !isClosedStatus(t.status)) {
      openChildCount[t.parent_todo_id] = (openChildCount[t.parent_todo_id] ?? 0) + 1;
    }
  }
  const map: Record<string, RowState> = {};
  for (const t of todos) {
    if (isClosedStatus(t.status)) {
      map[t.id] = 'done';
      continue;
    }
    map[t.id] = (openChildCount[t.id] ?? 0) > 0 ? 'blocked' : 'ready';
  }
  return map;
}

// classifyRowStates labels each todo in a flat project-task list as
// ready / blocked / done, driven by the seeded elka classify.todo.*
// rules so web and a future iOS port share one definition of "blocked".
//
// hasOpenChildren is the tree-relational fact each classify rule reads;
// it's computed here from the list's parent links (open == not
// done/cancelled) and injected via buildProjectTodoContext. Each row is
// labeled 'done' when classify.todo.closed matches, else 'blocked' when
// classify.todo.blocked matches, else 'ready'.
//
// Cold-start: when the rule cache is empty (rules not yet synced) the
// whole batch falls back to the imperative reference above, so behavior
// never regresses before the rules hydrate.
//
// Exported so Task 4's inbox-outlines store can reuse the exact same
// classification over its own flat per-project lists.
export function classifyRowStates(todos: ProjectTodo[]): Record<string, RowState> {
  const rules = latestRules();
  const closed = rules.get('classify.todo.closed');
  const blocked = rules.get('classify.todo.blocked');
  if (!rules.size || !closed?.filter?.predicate || !blocked?.filter?.predicate) {
    return classifyImperative(todos);
  }

  // open == not done/cancelled. hasOpenChildren(parentId) is true iff any
  // direct child is open. The classify rules only distinguish open vs not,
  // so direct-child openness is the exact fact "this row can't be closed yet".
  const openChildCount: Record<string, number> = {};
  for (const t of todos) {
    if (t.parent_todo_id && !isClosedStatus(t.status)) {
      openChildCount[t.parent_todo_id] = (openChildCount[t.parent_todo_id] ?? 0) + 1;
    }
  }

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const host = defaultHostFrame('', tz, []);
  const map: Record<string, RowState> = {};
  for (const t of todos) {
    // ProjectTodo widens sort_order to `number | null`; the walker's Todo
    // shape wants `number | undefined`. The classify rules don't read
    // sort_order, but normalize so the context builder's signature is
    // satisfied without an `any` cast.
    const todoForCtx = { ...t, sort_order: t.sort_order ?? undefined };
    const ctx = buildProjectTodoContext(todoForCtx, host, (openChildCount[t.id] ?? 0) > 0);
    if (evalFilter(closed.filter.predicate, ctx)) {
      map[t.id] = 'done';
    } else if (evalFilter(blocked.filter.predicate, ctx)) {
      map[t.id] = 'blocked';
    } else {
      map[t.id] = 'ready';
    }
  }
  return map;
}

// classifyPlanned returns the set of todo ids that match the seeded elka
// classify.todo.planned rule — an open todo with a due_at set. The "Planned"
// badge in the inbox hierarchy is shown for exactly these rows. Driven by the
// same elka pattern as classifyRowStates so web and a future iOS port share
// one definition of "planned". Cold-start fallback (rule cache empty): open
// status AND due_at != null, mirroring the seed body imperatively.
export function classifyPlanned(todos: ProjectTodo[]): Set<string> {
  const out = new Set<string>();
  const rules = latestRules();
  const planned = rules.get('classify.todo.planned');
  if (!rules.size || !planned?.filter?.predicate) {
    for (const t of todos) {
      if (!isClosedStatus(t.status) && t.due_at != null) out.add(t.id);
    }
    return out;
  }

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const host = defaultHostFrame('', tz, []);
  for (const t of todos) {
    const todoForCtx = { ...t, sort_order: t.sort_order ?? undefined };
    // hasOpenChildren is irrelevant to classify.todo.planned; pass false.
    const ctx = buildProjectTodoContext(todoForCtx, host, false);
    if (evalFilter(planned.filter.predicate, ctx)) out.add(t.id);
  }
  return out;
}

// ─── Inbox-outlines selectors (Task 4) ────────────────────

const selectInboxOutlines = createSelector(
  [selectProjectsState],
  (s) => s.inboxOutlines,
);

// Active projects (group headers), in server order.
export const selectInboxOutlineProjects = createSelector(
  [selectInboxOutlines],
  (io): Project[] =>
    io.projectIds.map((id) => io.projectsById[id]).filter(Boolean),
);

export const selectInboxOutlinesLoading = (state: StateWithProjects): boolean =>
  getProjectsState(state).inboxOutlines.loading;

// Per-project flat todo list for the inbox hierarchy. Memoized per projectId
// so each group's flatten/classify only recomputes when that project's bucket
// changes.
export const selectInboxOutlineTodos = (projectId: string) =>
  createSelector([selectInboxOutlines], (io): ProjectTodo[] => {
    const bucket = io.byProject[projectId];
    if (!bucket) return [];
    return bucket.todoIds.map((id) => bucket.todosById[id]).filter(Boolean);
  });

// selectProjectRowStateByTodo classifies the rows of `projectId` via the
// shared elka classify.todo.* rules. It resolves against the currently
// loaded single `detail` slice — when the detail slice holds `projectId`,
// its todos are classified; otherwise an empty map is returned (the
// inbox-outlines store, added in Task 4, supplies its own per-project
// lists to classifyRowStates directly). This replaces the imperative
// selectRowStateByTodo so Outline and the inbox view share one
// definition of "blocked".
export const selectProjectRowStateByTodo = (projectId: string) =>
  createSelector([selectProjectsState], (s): Record<string, RowState> => {
    if (s.detail.projectId !== projectId) return {};
    const todos = s.detail.todoIds
      .map((id) => s.detail.todosById[id])
      .filter(Boolean);
    return classifyRowStates(todos);
  });
